<template>
  <div class="products-page">

    <h1 class="page-title">所有商品</h1>

    <!-- 搜尋 + 排序區塊 -->
    <div class="top-bar">
      <!-- 關鍵字搜尋輸入框 -->
      <div class="search-bar">
        <input type="text" v-model="searchQuery" placeholder="輸入關鍵字搜尋" @input="applySearch" />
      </div>

      <!-- 排序下拉選單 -->
      <div class="sort-bar">
        <select id="sortSelect" v-model="selectedSort" @change="applySort" class="modern-dropdown">
          <option value="viewsDesc">最多觀看數</option>
          <option value="salesDesc">最熱賣商品</option>
          <option value="priceAsc">價格低到高</option>
          <option value="priceDesc">價格高到低</option>
          <option value="managerRec">店長最推薦</option>
        </select>
      </div>
    </div>

    <!-- 分類篩選區(範例: 按鈕) -->
    <div class="category-filter">
      <!-- "全部" 按鈕 (可選) -->
      <button :class="{ active: selectedCategory === null }" @click="selectedCategory = null">
        全部
      </button>
      <!-- 動態渲染 productTypes -->
      <button v-for="(pt) in productsTypes" :key="pt.id" :class="{ active: selectedCategory === pt.id }"
        @click="selectedCategory = pt.id">
        {{ pt.type_name }}
      </button>
    </div>


    <!-- SGS 檢驗報告區 -->
    <div v-if="homeStore.report_001" v-html="homeStore.report_001" class="products-sgs-wrap"></div>

    <!-- 活動公告橫幅 -->
    <div v-for="p in promotions" :key="p.id" class="promo-banner">
      <span class="promo-banner__icon">📣</span>
      <span class="promo-banner__tag">{{ p.content }}</span>
    </div>

    <!-- 滿額贈＋折扣券：「買越多送越多」階梯公告（直接讀後台「活動管理」，與購物車/結帳同源） -->
    <PromoLadder :gifts="thresholdGifts" :coupons="coupons" style="margin-bottom: 14px;" />

    <!-- 商品列表 (CSS Grid) -->
    <div class="product-grid">
      <div v-for="(product) in displayedProducts" :key="product.product_id" class="product-card">
        <!-- 商品圖片 -->
        <router-link :to="`/product/${product.product_id}`" class="product-image-link">
          <div class="image-wrapper">
            <img :src="product.image_url" :alt="product.title" />
             <!-- 顯示新品徽章 -->
             <span v-if="product.is_new == 1" class="badge star new">新品</span>
            <!-- 顯示熱銷徽章 -->
            <span v-if="product.is_hot == 1" class="badge star hot" :class="{'addbadgetop': product.is_new == 1 && product.is_hot == 1 }">熱銷</span>
            </div>
        </router-link>

        <!-- 商品資訊 -->
        <div class="product-info">
          <router-link :to="`/product/${product.product_id}`" class="product-name">
            {{ product.title }}
          </router-link>
          <p class="product-desc" v-html="highlightDesc(product.description)"></p>
          <div class="product-pricing">
            <span class="original-price" v-if="product.price !== product.discount_price">
              NT${{ product.price }}
            </span>
            <span class="current-price">
              NT${{ product.discount_price }}
            </span>
            <span v-if="product.price && product.discount_price && product.price !== product.discount_price"
                  class="discount-badge">
              {{ (Math.round(product.discount_price / product.price * 100) / 10).toFixed(1).replace(/\.0$/, '') }}折
            </span>
          </div>
          <!-- 券後價 -->
          <div v-if="bestCoupon(product.discount_price)" class="coupon-after-price">
            🏷️ 券後 <strong>NT${{ Math.round(product.discount_price * Number(bestCoupon(product.discount_price).discount_rate) / 10).toLocaleString() }}</strong>
            <span class="coupon-after-hint">（滿{{ bestCoupon(product.discount_price).min_amount }}享{{ Number(bestCoupon(product.discount_price).discount_rate) }}折）</span>
            <span v-if="bestCoupon(product.discount_price).days_left != null" class="coupon-after-expire">⏳ 倒數{{ bestCoupon(product.discount_price).days_left }}天</span>
          </div>

          <div class="stock-sold-wrapper" v-if="product.sold_quantity > 0">
            <div class="info-box sold-box">
              <i class="fas fa-fire-alt"></i>
              <span class="label">已售出&ensp;</span>
              <span class="number">{{ product.sold_quantity }}</span>
              <span class="label">&ensp;{{ product.unit }}</span>
            </div>
            <!-- <div class="info-box stock-box">
              <i class="fas fa-box-open"></i>
              <span class="label">剩餘庫存&ensp;</span>
              <span class="number">{{ product.stock }}</span>
              <span class="label">&ensp;{{ product.unit }}</span>
            </div> -->
          </div>

        </div>

        <!-- 加入購物車按鈕 -->
        <button class="add-to-cart-button" @click="openCartModal(product)">
          加入購物車
        </button>
      </div>
    </div>

    <!-- CartModal (與 Home.vue 相同作法) -->
    <CartModal v-if="showCartModal" :product="currentProduct" :isMobile="isMobile" @close-modal="closeModal"
      @add-to-cart="handleAddToCart" />
  </div>
</template>

<script>
import CartModal from '@/components/CartModal.vue'
import { useProductsStore } from '@/store/productsStore'
import { mapActions } from 'vuex'
import { useToastStore } from '@/store/toastStore.js'
import { useHomeStore } from '@/store/homeStore'
import axios from 'axios'
import { loadGifts, loadCoupons } from '@/utils/promoData'
import PromoLadder from '@/components/PromoLadder.vue'
export default {
  name: 'ProductsPage',
  components: {
    CartModal,
    PromoLadder
  },
  setup() {
    const toastStore = useToastStore()
    const homeStore = useHomeStore()
    return {
      toastStore,
      homeStore,
    }
  },
  data() {
    return {
      productList: [],
      productsTypes: [],
      selectedCategory: null,  // 分類
      searchQuery: '',           // ★ 新增：關鍵字
      selectedSort: 'managerRec',
      showCartModal: false,
      currentProduct: null,
      isMobile: false,
      coupons: [],
      promotions: [],
      thresholdGifts: []
    }
  },
  computed: {
    // 先依分類
    filteredByCategory() {
      if (!this.selectedCategory) {
        return this.productList
      }
      return this.productList.filter(p => p.product_type === this.selectedCategory)
    },

    // 再依搜尋
    filteredBySearch() {
      let arr = [...this.filteredByCategory]
      if (this.searchQuery.trim() !== '') {
        const keyword = this.searchQuery.toLowerCase()
        arr = arr.filter(p => {
          // 你想要比對 product.title 或 product.description
          // 都轉成小寫後判斷是否包含 keyword
          return (
            p.title.toLowerCase().includes(keyword) ||
            (p.description && p.description.toLowerCase().includes(keyword))
          )
        })
      }
      return arr
    },
    // 最後再依排序
    displayedProducts() {
      let sorted = [...this.filteredBySearch] // 改從 filteredBySearch 取得
      switch (this.selectedSort) {
        case 'viewsDesc':
          sorted.sort((a, b) => b.views - a.views)
          break
        case 'salesDesc':
          sorted.sort((a, b) => b.sales - a.sales)
          break
        case 'priceAsc':
          sorted.sort((a, b) => a.discount_price - b.discount_price)
          break
        case 'priceDesc':
          sorted.sort((a, b) => b.discount_price - a.discount_price)
          break
        case 'managerRec':
          sorted.sort((a, b) => (b.manager_rank || 0) - (a.manager_rank || 0))
          break
      }
      return sorted
    }
  },
  methods: {
    ...mapActions(['addProductToCart']), // ★ 新增：將 addProductToCart 映射到 methods
    handleAddToCart({ product, quantity }) {
      // (A) 呼叫 Vuex 來做真正的加入購物車
      this.addProductToCart({ product, quantity })
      // (B) 顯示提示 (以下是示範)
     // this.toastStore.addToast(`成功加入購物車：${product.title}`)
      // (C) 關閉 Modal
      this.closeModal();
    },
    openCartModal(product) {
      this.currentProduct = product
      this.showCartModal = true
      document.body.classList.add('no-scroll')
    },
    closeModal() {
      this.showCartModal = false
      document.body.classList.remove('no-scroll')
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    // 找出該商品價格可適用的最佳折扣券（折數最低 = 折扣最大）
    // 把描述裡「買X送X」類促銷文字自動高亮
    highlightDesc(text) {
      if (!text) return ''
      return text.replace(/(買\d+[^\s，,、。！!]{0,4}送\d+[^\s，,、。！!]{0,4})/g,
        '<span class="desc-promo">$1</span>')
    },
    bestCoupon(price) {
      const applicable = this.coupons.filter(c => price >= c.min_amount)
      if (applicable.length === 0) return null
      return applicable.reduce((best, c) => Number(c.discount_rate) < Number(best.discount_rate) ? c : best)
    },
    applySort() {
      // 當使用者更改下拉選單時會觸發
      // 這裡其實不用做任何事，因為 displayedProducts() 會自動計算
      // 但可以在此 console.log() 觀察
      console.log('selectedSort:', this.selectedSort)
    },
    // (Demo) 假裝從後端撈資料
    loadProducts() {
      // this.productList = [
      //   {
      //     product_id: 1,
      //     title: '保濕面膜',
      //     category: 'mask',
      //     image_url: 'https://via.placeholder.com/400x400?text=Mask1',
      //     price: 500,
      //     discount_price: 350,
      //     description: '深層保濕，讓肌膚水嫩光滑',
      //     views: 1200,
      //     sales: 98,
      //     manager_rank: 5
      //   },
      //   {
      //     product_id: 2,
      //     title: '亮白面膜',
      //     category: 'mask',
      //     image_url: 'https://via.placeholder.com/400x400?text=Mask2',
      //     price: 400,
      //     discount_price: 300,
      //     description: '淡化暗沉，提升肌膚光澤',
      //     views: 600,
      //     sales: 75,
      //     manager_rank: 3
      //   },
      //   {
      //     product_id: 3,
      //     title: '保濕精華液',
      //     category: 'essence',
      //     image_url: 'https://via.placeholder.com/400x400?text=Essence1',
      //     price: 700,
      //     discount_price: 600,
      //     description: '高效保水，改善乾燥肌膚',
      //     views: 280,
      //     sales: 30,
      //     manager_rank: 4
      //   },
      // ]
    }
  },
  created: async function () {
    const productsStore = useProductsStore()
    await productsStore.fetchProductsData()
    this.productList = productsStore.products
    this.productsTypes = productsStore.productsTypes
    // 折扣券＋滿額贈同時載入、一起顯示（階梯公告才不會先出現一半）
    try {
      const [cs, gs] = await Promise.all([loadCoupons(), loadGifts()])
      this.coupons = cs
      this.thresholdGifts = gs
    } catch (e) { /* 靜默失敗，不影響商品頁 */ }
    // 載入活動公告
    try {
      const rp = await axios.get('/api/load/promotions')
      if (rp.data.success) this.promotions = rp.data.promotions
    } catch (e) { /* 靜默失敗 */ }
  },
  mounted() {
    // 確保 homeStore 已載入（直接進商品頁或 F5 時 store 可能是空的）
    this.homeStore.fetchHomeData()

    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    // this.loadProducts()
    // this.fetchProducts();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  }
}
</script>

<style scoped>
.products-page {
  max-width: 1200px;
  /* 視需求調整 */
  margin: 0 auto;
  padding: 20px;
  text-align: center;
  box-sizing: border-box;
}

.page-title {
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: #333;
}

/* 排序下拉選單 */
.sort-bar {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  /* label 與 select 之間的距離 */
}

#sortSelect {
  outline: none;
  border: 1px solid #ccc;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: border-color 0.3s;
}

#sortSelect:hover {
  border-color: #cc4a00;
}

/* 或者可使用更炫的漸層、icon等方式，這裡維持簡潔風 */

/* 分類篩選: 按鈕列 */
/* 活動公告橫幅 */
.promo-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: linear-gradient(90deg, #fff8f0 0%, #fff3e0 100%);
  border: 1px solid #f0c060;
  border-radius: 8px;
  padding: 10px 18px;
  margin-bottom: 6px;
  font-size: 0.93rem;
  cursor: default;
}
.promo-banner__icon { font-size: 1.1rem; }
.promo-banner__tag { color: #cc4400; font-weight: 600; }

/* 折扣券橫幅 */
.coupon-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  background: linear-gradient(90deg, #fff3e8, #fff8f3);
  border: 1.5px solid #ffb347;
  border-radius: 10px;
  padding: 10px 18px;
  margin-bottom: 20px;
  font-size: 0.93rem;
}
.coupon-banner__icon { font-size: 1.1rem; }
.coupon-banner__label { font-weight: 700; color: #cc4400; }
.coupon-banner__tag {
  color: #aa3300;
  font-weight: 600;
}
.coupon-banner__expire {
  color: #c0392b;
  font-size: 0.82rem;
  font-weight: 700;
}
.gift-banner { background: linear-gradient(90deg, #eefaf1, #f6fcf8); border-color: #7cc79a; margin-bottom: 6px; }
.gift-banner .coupon-banner__label { color: #1f7a45; }
.gift-banner .coupon-banner__tag   { color: #1f6b3c; }

/* 商品卡券後價 */
.coupon-after-price {
  font-size: 0.82rem;
  color: #cc4400;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.coupon-after-price strong {
  font-size: 1rem;
}
.coupon-after-hint {
  color: #767676;
  font-size: 0.75rem;
}
.coupon-after-expire {
  color: #c0392b;
  font-size: 0.75rem;
  font-weight: 700;
}

.category-filter {
  margin-bottom: 20px;
}

.category-filter button {
  margin: 4px;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid #ccc;
  background-color: #f5f5f5;
  color: #333;
  cursor: pointer;
  transition: all 0.3s;
}

.category-filter button:hover {
  background-color: #cc4a00;
  color: #fff;
}

.category-filter .active {
  background-color: #cc4a00;
  color: #fff;
  border-color: #cc4a00;
}

/* SGS 區塊外距 */
.products-sgs-wrap {
  margin: 24px 0;
  border-radius: 12px;
  overflow: hidden;
}

/* 商品列表：最多 4 欄 (桌機)；手機 1 欄 */
.product-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  /* 一列最多顯示 4 欄 */
  gap: 20px;
  align-items: start;
}

/* 手機 RWD: 一行只顯示 1 個 */
@media (max-width: 768px) {
  .product-grid {
    grid-template-columns: 1fr;
  }
}

/* 單張商品卡片 */
.product-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s, box-shadow 0.3s;
}

.product-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.12);
}

/* 商品圖片 */
.product-image-link img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 商品資訊 */
.product-info {
  padding: 16px;
  text-align: left;
  flex: 1;
  /* 撐開，使按鈕可貼底 */
}

.product-name {
  font-weight: 600;
  color: #333;
  text-decoration: none;
  margin-bottom: 4px;
  display: inline-block;
  font-size: 1.1rem;
  transition: color 0.3s;
}

.product-name:hover {
  color: #cc4a00;
}

.product-desc {
  font-size: 0.9rem;
  color: #777;
  margin-bottom: 8px;
  line-height: 1.6;
}
.product-desc :deep(.desc-promo) {
  display: inline-block;
  background: linear-gradient(135deg, #c2410c, #cc4a00);
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 1px 8px;
  border-radius: 20px;
  margin: 0 2px;
  white-space: nowrap;
  box-shadow: 0 1px 4px rgba(255,102,0,0.3);
}

.product-pricing {
  margin-bottom: 12px;
}

.original-price {
  font-size: 1.2rem;
  text-decoration: line-through;
  color: #767676;
  margin-right: 8px;
}

.current-price {
  font-size: 1.6rem;
  color: #e60000;
  font-weight: 700;
}
.discount-badge {
  display: inline-block;
  background: #e60000;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 4px;
  margin-left: 6px;
  vertical-align: middle;
  letter-spacing: 0.5px;
}

/* 按鈕(貼底) */
.add-to-cart-button {
  border: none;
  border-top: 1px solid #eee;
  background: #cc4a00;
  color: #fff;
  padding: 10px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s;
  border-radius: 0 0 8px 8px;
}

.add-to-cart-button:hover {
  background: #ff5500;
}

.top-bar {
  display: flex;
  /* 水平排列 */
  flex-wrap: wrap;
  /* 當螢幕太窄時，容許換行 */
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  /* 左右區塊之間的間距 */
  margin-bottom: 1rem;
}

.search-bar {
  display: flex;
  align-items: center;
  /* 垂直置中 */
  flex: 1;
  /* 讓搜尋欄可以自動擴展，保持彈性 */
  max-width: 400px;
  /* 可視需求限制寬度，避免過長 */
}

.search-bar input {
  padding: 8px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  /* 佔滿 search-bar 區域 */
  box-sizing: border-box;
}

.search-bar input:focus {
  outline: none;
  border-color: #cc4a00;
}

.sort-bar {
  display: flex;
  align-items: center;
  /* 如果想讓排序下拉固定寬度或靠右，可加 margin-left auto 之類 */
}

.sort-bar select {
  outline: none;
  border: 1px solid #ccc;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: border-color 0.3s;
}

.sort-bar select:hover {
  border-color: #cc4a00;
}

/* 手機 RWD: 若螢幕窄時，可讓 .top-bar 改為一列一列排列 */
@media (max-width: 768px) {
  .top-bar {
    flex-direction: column;
    /* 垂直堆疊 */
    align-items: flex-start;
    /* 讓搜尋與排序分2行 */
    gap: 0.5rem;
  }

  .search-bar,
  .sort-bar {
    width: 100%;
    max-width: none;
    /* 移除 max-width 限制，以充滿容器 */
  }
}


.image-wrapper {
  position: relative;
}

/* 基礎徽章樣式 */
.badge {
  /* 把徽章改大一點 */
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;     /* 讓文字可置中 */
  justify-content: center;
  align-items: center;
  color: #fff;
  font-size: 1rem;   /* 文字大一點 */
  font-weight: bold; /* 文字粗一點 */
  text-align: center;
  z-index: 1;
  /* 你可以再加點陰影 */
  box-shadow: 0 3px 5px rgba(0,0,0,0.3);
  /* 若要圓潤可加 border-radius: 4px; */
}

/* 新品徽章樣式 */
.badge.new {
  background-color: #1e8449; /* 可調整為你喜歡的顏色 */
}

/* 熱銷徽章樣式 */
.badge.hot {
  background-color: #c0392b;
  color: #ffffff;           /* 黃色文字 */
}


.addbadgetop{
  top: 70px;
}

/* 星形關鍵：使用 clip-path */
.badge.star {
  position: absolute;
  right: 10px;
  width: 65px; 
  height: 55px;
  font-size: 1.2rem;
  font-weight: bold;
  text-align: center;
  line-height: 120px;       /* 讓文字置中 */
  z-index: 1;
  clip-path: polygon(
    45% 0%,   /* 多邊形點：可依需求做更多鋸齒 */
    60% 10%,
    90% 0%,
    80% 30%,
    100% 50%,
    80% 70%,
    90% 100%,
    60% 90%,
    45% 100%,
    40% 90%,
    10% 100%,
    20% 70%,
    0% 50%,
    20% 30%,
    10% 0%,
    40% 10%
  );
  -webkit-clip-path: polygon(
    45% 0%,
    60% 10%,
    90% 0%,
    80% 30%,
    100% 50%,
    80% 70%,
    90% 100%,
    60% 90%,
    45% 100%,
    40% 90%,
    10% 100%,
    20% 70%,
    0% 50%,
    20% 30%,
    10% 0%,
    40% 10%
  );
}
</style>
<style scoped>
/**
 * 商品庫存 / 已售出
 */
.stock-sold-wrapper {
  display: flex;
  gap: 20px;  
  margin: 12px 0;
  .info-box {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 6px;
    background: linear-gradient(120deg, #fff 0%, #f3f3f3 100%);
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    transition: box-shadow 0.3s ease, background 0.3s ease;
    cursor: default; 
    i {
      margin-right: 6px;
      font-size: 1.2rem;
      color: #444;
      transition: color 0.3s;
    }
    .number {
      font-size: 1.3rem; 
      font-weight: bold; 
      margin-right: 4px;
      color: #222;
    }
    .label {
      font-size: 0.85rem;
      color: #666;
    }
    &:hover {
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      background: linear-gradient(120deg, #fafafa 0%, #f0f0f0 100%);
      i {
        color: #cc4a00; 
      }
    }
  }
  .sold-box {
    background: linear-gradient(120deg, #fff5f2 0%, #fce6e0 100%);
    &:hover {
      background: linear-gradient(120deg, #feece7 0%, #fbded6 100%);
    }
    i {
      color: #c0392b; 
    }
  }
  .stock-box {
    background: linear-gradient(120deg, #f5fff2 0%, #e4fce0 100%);
    &:hover {
      background: linear-gradient(120deg, #ebffe6 0%, #d5fcd2 100%);
    }
    i {
      color: #1e8449; 
    }
  }
}
</style>